import type { ReactNode } from 'react'
import { podeEmAlgumTenant, useAuthStore, useCan } from '@/store/auth'
import { Forbidden } from './Forbidden'

interface RequirePermissionProps {
  /** Permissão no formato "recurso:acao" (ex.: "usuarios:manage"). */
  perm: string
  /**
   * true para rotas de plataforma: a permissão é checada contra
   * global_permissions/is_admin (não escopo de tenant). Rotas globais
   * (admin/auditoria global/finops/security) são gateadas no backend por
   * permissão global — o guard só espelha isso no cliente.
   */
  global?: boolean
  /**
   * Roles que concedem a rota SOZINHAS, além da permissão. Serve para espelhar
   * rota cujo backend gateia por FUNÇÃO e não por permissão — sem isso o guard
   * fica mais restritivo que a API, que é justamente o que a docstring abaixo
   * proíbe.
   *
   * Caso que motivou: `/admin/registrations`. A API pede role `gestor`/`admin`
   * (`_require_gestor`, api.py) e o serviço já limita os dados às organizações
   * que a pessoa responde (`responsible_user_id`). A tela, porém, exigia
   * `usuarios:manage` global — permissão que abre outras QUATRO telas, entre
   * elas a de editar perfis de permissão. O gestor responsável pela
   * organização levava 403 numa tela que a API deixaria ele usar.
   */
  anyRole?: string[]
  /**
   * true para tela que lista dado de VARIOS tenants e nao depende do seletor
   * (a frota do robo, TASK-11262): basta a permissao em algum tenant. Sem
   * isso, quem so tem `robo:read` noutro cliente levava 403 na tela que a API
   * deixaria ele usar.
   */
  anyTenant?: boolean
  children: ReactNode
}

/**
 * Guard de rota por permissão (M-07 / TASK-2831). Defense-in-depth: o backend
 * permanece a autoridade — este guard NUNCA é mais restritivo que ele e, em
 * dúvida, libera (renderiza children) para não quebrar navegação legítima.
 *
 * Em falta de permissão renderiza <Forbidden/> (403 amigável, sem redirect —
 * evita loop e preserva a URL).
 */
export function RequirePermission({ perm, global = false, anyRole, anyTenant = false, children }: RequirePermissionProps) {
  const user = useAuthStore((s) => s.user)
  const can = useCan()

  // Sem user não deveria ocorrer dentro de RequireAuth; por segurança, libera
  // (o RequireAuth já trata não-autenticado).
  if (!user) return <>{children}</>

  const [resource, action] = perm.split(':')

  // Role concede sozinha, quando a rota declara `anyRole`. Vem antes do resto
  // porque é um eixo INDEPENDENTE da permissão — é assim que o backend decide
  // nessas rotas, e o guard existe para espelhar o backend, não para inventar
  // uma regra própria.
  if (anyRole?.length) {
    const codes = user.role_codes ?? []
    if (anyRole.some((r) => codes.includes(r))) return <>{children}</>
  }

  let allowed: boolean
  if (global) {
    // Rotas de plataforma: apenas is_admin ou permissão global concede.
    const globals = user.global_permissions ?? []
    allowed = !!user.is_admin || globals.includes('admin:full') || globals.includes(perm)
  } else if (anyTenant) {
    allowed = podeEmAlgumTenant(user, can, resource, action)
  } else {
    // Rotas tenant-scoped: reusa a lógica canônica do useCan (is_admin,
    // admin:full global, permissão global, ou permissão no tenant selecionado).
    allowed = can(resource, action)
  }

  return allowed ? <>{children}</> : <Forbidden />
}
